import React, { useEffect, useRef, useState } from 'react';
import Icon from 'src/components/common/Icon';
import { FilterPill, OptionItem, OptionList } from './Transactions.styles';

export interface FilterSelectOption<T> {
    value: T;
    label: string;
}

interface FilterSelectProps<T> {
    dataId: string;
    /** Icono de la pastilla (nombre del set de iconos del dashboard). */
    icon: string;
    /** Prefijo fijo del control, p. ej. "Estado de pago:". */
    label?: string;
    /** Nombre accesible cuando el control no lleva prefijo visible. */
    ariaLabel?: string;
    value: T;
    options: FilterSelectOption<T>[];
    onChange: (value: T) => void;
    /** Apaga el control sin esconderlo. El motivo viaja en `disabledHint`. */
    disabled?: boolean;
    disabledHint?: string;
}

/**
 * Desplegable de filtro con forma de pastilla.
 *
 * No usa `<select>` nativo por dos razones que se notan en el panel:
 *   - el `select` solo ocupa el ancho de su texto, así que la flecha y el resto
 *     de la pastilla quedaban como zona muerta;
 *   - la posición de su lista la decide el navegador, que la abre encima del
 *     control y tapa el filtro.
 *
 * El teclado se maneja en el contenedor para que también responda cuando el
 * foco está en una opción: flechas mueven, Enter elige, Escape cierra.
 */
const FilterSelect = <T extends string | number>({
    dataId,
    icon,
    label,
    ariaLabel,
    value,
    options,
    onChange,
    disabled = false,
    disabledHint,
}: FilterSelectProps<T>) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedIndex = options.findIndex((option) => option.value === value);
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : '';

    // Un desplegable abierto mientras la atención ya está en otra parte solo
    // estorba: cualquier clic afuera lo cierra.
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handlePointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    const openList = () => {
        setActiveIndex(selectedIndex < 0 ? 0 : selectedIndex);
        setOpen(true);
    };

    const choose = (option: FilterSelectOption<T>) => {
        setOpen(false);
        if (option.value !== value) {
            onChange(option.value);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) {
                openList();
                return;
            }
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((index) => (index + step + options.length) % options.length);
            return;
        }
        // Con la lista abierta, Enter elige en lugar de reabrir el disparador.
        if (open && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            choose(options[activeIndex]);
        }
    };

    return (
        // El botón apagado no recibe eventos de mouse, así que el motivo va en
        // el envoltorio para que el tooltip aparezca al pasar por encima.
        <FilterPill
            ref={containerRef}
            $open={open}
            onKeyDown={handleKeyDown}
            title={disabled ? disabledHint : undefined}
        >
            <button
                type="button"
                className="pill-trigger"
                data-id={dataId}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={label ? undefined : ariaLabel}
                disabled={disabled}
                onClick={() => (open ? setOpen(false) : openList())}
            >
                <span className="pill-icon">
                    <Icon icon={icon} size={13} />
                </span>
                {label}
                <span className="pill-value">{selectedLabel}</span>
                <span className="pill-caret">
                    <Icon icon="pcom-icon-chevron-down" size={10} />
                </span>
            </button>

            {open && (
                <OptionList
                    role="listbox"
                    data-id={`${dataId}-list`}
                    aria-label={label ?? ariaLabel}
                >
                    {options.map((option, index) => (
                        <OptionItem
                            key={String(option.value)}
                            type="button"
                            role="option"
                            tabIndex={-1}
                            aria-selected={option.value === value}
                            data-id={`${dataId}-option-${option.value === '' ? 'all' : option.value}`}
                            $active={index === activeIndex}
                            $selected={option.value === value}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => choose(option)}
                        >
                            {option.label}
                        </OptionItem>
                    ))}
                </OptionList>
            )}
        </FilterPill>
    );
};

export default FilterSelect;
